'use client';

import { CheckboxIcon, RightIcon } from '@/_assets/icon-components';
import { getCheckPointDeliveryZones } from '@/_service/zone.service';
import {
  activatePoint,
  onOpenMap,
  setDeliveryZone,
} from '@/_store/common/common.slice';
import { IPoint } from '@/_store/common/types';
import { IRedux } from '@/_store/types';
import { CloseButton } from '@/components/ui/close-button';
import { RadioCardItem, RadioCardRoot } from '@/components/ui/radio-card';
import { toaster } from '@/components/ui/toaster';
import {
  Box,
  Button,
  Dialog,
  HStack,
  Portal,
  Stack,
  StackSeparator,
  Text,
  VStack,
} from '@chakra-ui/react';
import { ChevronRight, MapPin } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { FC, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IProps } from '../_types';
import { useActiveBrand } from '@/_hooks/useActiveBrand';
import { mapDesktopLog } from '@/_components/Map/mapDesktopLog';

const Desktop: FC<IProps> = ({ isOpen, onClose, isModal = false }) => {
  const t = useTranslations();
  const dispatch = useDispatch();
  const shipperId = useActiveBrand();
  const { points } = useSelector((state: IRedux) => state.common);
  const deliveryZone = useSelector(
    (state: IRedux) => state.common.deliveryZones?.[shipperId] ?? null,
  );

  const [value, setValue] = useState<string>('');

  const handleTariffGeozone = useCallback(
    async (lat: number, lon: number, point: IPoint) => {
      try {
        const data = await getCheckPointDeliveryZones({ lat, lon }, shipperId);
        if (data?.data?.all_count > 0 && !data?.data?.result?.length) {
          toaster.create({
            title: t('address_not_in_service_area'),
            type: 'error',
          });
        } else {
          const currentZone = data?.data?.result?.[0];
          if (currentZone?.id && shipperId) {
            dispatch(
              setDeliveryZone({
                shipper_id: shipperId,
                zone: {
                  id: currentZone?.id,
                  min_order_amount: currentZone?.min_order_amount,
                },
              }),
            );
          } else if (deliveryZone?.id && shipperId) {
            dispatch(setDeliveryZone({ shipper_id: shipperId, zone: null }));
          }
          dispatch(activatePoint(point.id));
          onClose();
        }
      } catch (error) {
        console.error(error);
      }
    },
    [dispatch, t, shipperId, deliveryZone?.id],
  );

  const handleSelected = useCallback(
    (point: IPoint) => {
      setValue(point.id);
      if (point?.location?.length === 2) {
        const [lat, lon] = point.location;
        handleTariffGeozone(lat, lon, point);
      }
    },
    [handleTariffGeozone],
  );

  const handleClick = () => {
    mapDesktopLog('1 points-add-address');
    dispatch(onOpenMap());
    onClose();
  };

  const activeAddress = points?.find((el) => el.isActive);

  if (points?.length > 0) {
    if (isModal) {
      return (
        <Dialog.Root lazyMount open={isOpen} onOpenChange={onClose}>
          <Portal>
            <Dialog.Backdrop />
            <Dialog.Positioner>
              <Dialog.Content
                bg={'background.card'}
                color={'text'}
                p={4}
                rounded={12}
              >
                <Dialog.Header
                  display={'flex'}
                  alignItems={'center'}
                  justifyContent={'space-between'}
                  p={0}
                  pb={3}
                >
                  <Dialog.Title>
                    <Text fontSize='xl' fontWeight='bold'>
                      {t('addresses')}
                    </Text>
                  </Dialog.Title>
                  <Dialog.CloseTrigger asChild>
                    <CloseButton size='sm' />
                  </Dialog.CloseTrigger>
                </Dialog.Header>
                <Dialog.Body p={0}>
                  <VStack
                    display={{ base: 'none', lg: 'flex' }}
                    gap={4}
                    w='full'
                  >
                    <RadioCardRoot
                      unstyled
                      w='full'
                      value={value}
                      onValueChange={(e: any) => setValue(e.value)}
                    >
                      <Stack w='full' gap={2}>
                        {points?.map((point) => (
                          <HStack
                            key={point.id}
                            px={4}
                            py={3}
                            w='full'
                            rounded={16}
                            bg='background'
                            cursor='pointer'
                            justifyContent='space-between'
                            fontSize={{ base: 'sm', lg: 'md' }}
                            onClick={() => handleSelected(point)}
                          >
                            <Text
                              truncate
                              maxW={'450px'}
                              style={{ cursor: 'pointer' }}
                            >
                              {point.address}
                            </Text>
                            <RadioCardItem
                              cursor='pointer'
                              value={point.id}
                              id={point.id}
                              indicator={false}
                              border='none'
                              icon={
                                activeAddress?.id === point.id ? (
                                  <CheckboxIcon color='var(--primary-default)' />
                                ) : (
                                  <Box bg='light' w={7} h={7} rounded='full' />
                                )
                              }
                            />
                          </HStack>
                        ))}
                      </Stack>
                    </RadioCardRoot>
                    <Button
                      w='full'
                      h='48px'
                      rounded={16}
                      variant='plain'
                      bg='light.200'
                      color='text'
                      justifyContent='space-between'
                      px={4}
                      _hover={{ bg: 'light.300' }}
                      size='lg'
                      onClick={handleClick}
                    >
                      <HStack gap={3}>
                        <MapPin
                          strokeWidth={1.3}
                          color='var(--primary-default)'
                        />
                        <Text fontWeight={500}>{t('add_new_address')}</Text>
                      </HStack>
                      <RightIcon color='text' />
                    </Button>
                  </VStack>
                </Dialog.Body>
              </Dialog.Content>
            </Dialog.Positioner>
          </Portal>
        </Dialog.Root>
      );
    } else {
      return (
        <VStack display={{ base: 'none', lg: 'flex' }} gap={2} w='full'>
          <Button
            px={3}
            w='full'
            h='48px'
            size='2xl'
            color='text'
            rounded={16}
            variant='subtle'
            justifyContent='space-between'
            onClick={handleClick}
          >
            <HStack gap={2}>
              <MapPin
                strokeWidth={1.5}
                color='var(--primary-default)'
                size={24}
              />
              <Text fontWeight={500} fontSize='md'>
                {t('add_new_address')}
              </Text>
            </HStack>
            <ChevronRight size={24} />
          </Button>

          {/* added addresses */}
          <RadioCardRoot
            unstyled
            w='full'
            value={value}
            onValueChange={(e: any) => setValue(e.value)}
          >
            <Stack separator={<StackSeparator />} w='full' gap={2}>
              {points?.map((point) => (
                <HStack
                  key={point.id}
                  px={3}
                  py={'6px'}
                  w='full'
                  rounded={16}
                  cursor='pointer'
                  justifyContent='space-between'
                  fontSize={{ base: 'sm', lg: 'md' }}
                  onClick={() => handleSelected(point)}
                >
                  <HStack>
                    <MapPin strokeWidth={1.5} size={24} />
                    <label htmlFor={point.id} style={{ cursor: 'pointer' }}>
                      <Text fontWeight={500} fontSize='md'>
                        {point.accommodation || point.address}
                      </Text>
                      {point.accommodation && (
                        <Text color='gray.600' fontSize='sm'>
                          {point.address}
                        </Text>
                      )}
                    </label>
                  </HStack>
                  <RadioCardItem
                    cursor='pointer'
                    value={point.id}
                    id={point.id}
                    indicator={false}
                    border='none'
                    icon={
                      activeAddress?.id === point.id ? (
                        <CheckboxIcon color='var(--primary-default)' />
                      ) : (
                        <Box bg='gray.400' w={7} h={7} rounded='full' />
                      )
                    }
                  />
                </HStack>
              ))}
            </Stack>
          </RadioCardRoot>
        </VStack>
      );
    }
  }

  return null;
};

export default Desktop;
